import { IAnyObject } from '../../interfaces'
import { User } from '../../models'
import { ApiCore } from './ApiCore'
import { NoAuthError, ServerError } from './ApiException'
import { IApiOptions } from './IApiOptions'

interface IResponse {
    [key: string]: string
}
export abstract class ApiClient extends ApiCore {
    protected constructor(options: IApiOptions) {
        super(options)
    }

    abstract appendHeaders(): Promise<HeadersInit>

    private buildQueryString(data: IAnyObject): string {
        const queryStr = Object.keys(data)
            .filter((key: string) => !!data[key])
            .map((key: string) => {
                if (data[key] instanceof Array) {
                    return `${key}=${encodeURI(JSON.stringify(data[key]))}`
                }

                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `${key}=${data[key]}`
            })
            .join('&')

        return queryStr ? '?' + queryStr : ''
    }

    private buildInput(path: string, query: IAnyObject = {}): string {
        return `${this.serviceBase}/${path}${this.buildQueryString(query)}`
    }

    async makeRequest(method: string, path: string, query: IAnyObject = {}, data?: any): Promise<any> {
        const headers = { ...this.appJsonHeader, ...(await this.appendHeaders()) }
        const body = data ? JSON.stringify({ ...data }) : null

        const response: Response = await fetch(this.buildInput(path, query), {
            method,
            body,
            headers
        })

        const { status } = response
        const responseBody = (await response.json()) as IResponse

        if (status === 401) {
            throw new NoAuthError(responseBody.error)
        } else if (status === 204) {
            return this.EmptyResponse
        } else if (status === 404) {
            throw new ServerError(responseBody, responseBody.error || 'NotFound')
        } else if (!status || status < 200 || status >= 300) {
            if (status === 400 && responseBody.error === 'User Access not Found') {
                new User().logout()

                return
            }
            throw new ServerError(body, responseBody.error)
        }

        return responseBody
    }

    protected isExpiredJWT(token: string): boolean {
        return Date.now() >= ApiClient.getJWTExpiration(token) - 2 * 60 * 1000
    }

    private static getJWTExpiration(token: string): number {
        const [base64Url] = token.split('.')
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        )
        const payload = JSON.parse(jsonPayload) as IAnyObject

        return (payload.exp as number) * 1000
    }
}
